import { InfoPageBackLink } from '@/components/layout/InfoPageBackLink';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-surface">
      <div className="ornamental-rule max-w-7xl mx-auto px-4 pt-6 sm:pt-8" />
      <div className="max-w-4xl mx-auto px-4 py-6 sm:py-8">
        <InfoPageBackLink />

        <div className="text-center mb-8">
          <span className="text-gold text-xs font-bold tracking-[0.2em] uppercase mb-4 block">
            Legal
          </span>
          <h1 className="text-3xl sm:text-4xl font-serif font-bold text-cream mb-4">
            Privacy Policy
          </h1>
          <p className="text-sm text-tertiary">Last updated: February 24, 2026</p>
        </div>

        <div className="prose-custom space-y-8">
          <Section title="1. Information We Collect">
            <p>When you create an account, we collect your username, email address, and password (stored in hashed form). When you use our services, we may also collect usage data such as pages visited, picks made, and transaction history.</p>
            <p>If you make a purchase through our payment processor (Jest), Jest collects your payment information directly. We do not store your credit card number or full payment details on our servers.</p>
          </Section>

          <Section title="2. How We Use Your Information">
            <p>We use the information we collect to:</p>
            <ul>
              <li>Provide, maintain, and improve FantasyPRIME</li>
              <li>Process transactions and send related information</li>
              <li>Send technical notices, updates, and support messages</li>
              <li>Track leaderboard standings and event participation</li>
              <li>Detect, prevent, and address fraud or technical issues</li>
            </ul>
          </Section>

          <Section title="3. Cookies and Tracking">
            <p>We use essential cookies to maintain your session and authentication state. We do not use third-party advertising cookies. Analytics data, if collected, is used solely to improve the platform experience.</p>
          </Section>

          <Section title="4. Third-Party Services">
            <p>We use the following third-party services:</p>
            <ul>
              <li><strong>Jest</strong> — Payment processing. Subject to <a href="https://jest.com/privacy" className="text-gold hover:underline" target="_blank" rel="noopener noreferrer">Jest&apos;s Privacy Policy</a>.</li>
              <li><strong>Vercel</strong> — Hosting and infrastructure.</li>
            </ul>
          </Section>

          <Section title="5. Data Retention">
            <p>We retain your account data for as long as your account is active. If you request account deletion, we will remove your personal information within 30 days, except where retention is required by law.</p>
          </Section>

          <Section title="6. Your Rights">
            <p>You may request access to, correction of, or deletion of your personal data by contacting us. Depending on your jurisdiction, you may have additional rights under applicable data protection laws (such as GDPR or CCPA).</p>
          </Section>

          <Section title="7. Security">
            <p>We implement reasonable security measures to protect your information, including encrypted passwords, HTTPS connections, and secure session management. However, no method of transmission over the Internet is 100% secure.</p>
          </Section>

          <Section title="8. Changes to This Policy">
            <p>We may update this privacy policy from time to time. We will notify you of any material changes by posting the updated policy on this page with a revised &quot;Last updated&quot; date.</p>
          </Section>

          <Section title="9. Contact">
            <p>If you have questions about this privacy policy, please contact us at <span className="text-gold">support@fantasyprime.com</span>.</p>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-xl font-serif font-bold text-cream mb-3">{title}</h2>
      <div className="text-warm-gray text-sm space-y-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_li]:text-warm-gray">
        {children}
      </div>
    </section>
  );
}
